import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Plus } from "lucide-react";
import { useAppDispatch } from "hooks";
import {
  approveWsApproval,
  fetchWsApproval,
  WsApprovalDetail,
} from "services/work-security-approvals.service";
import { formatPrettyDate } from "utils/helperFunctions";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { approvalKindKey } from "./approvals-helpers";
import { describePositionsError } from "features/positions/positions-errors";
import withAreaQuery from "features/work-security-areas/with-area-query";

/**
 * One sphere's approval of an employee's position-derived pareigybės
 * (route /app/:companyId/work-security/approvals/:approvalId — the link in the
 * approval email). The proposal is read-only: to change it the approver fixes
 * the position ↔ pareigybė links. They may add individual pareigybės of this
 * sphere, then «Patvirtinti» applies everything for this sphere at once.
 *
 * Shows nothing from the hiring questionnaire (medical / salary): approvers
 * may have no Personalas access, and the endpoint does not send any.
 */
export default function WsApprovalPanel() {
  const { companyId, approvalId } = useParams<{ companyId: string; approvalId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [approval, setApproval] = useState<WsApprovalDetail | null>(null);
  const [loadError, setLoadError] = useState<"forbidden" | "not_found" | "failed" | null>(null);
  const [addingIndividual, setAddingIndividual] = useState(false);
  const [individualIds, setIndividualIds] = useState<string[]>([]);
  const [approving, setApproving] = useState(false);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!companyId || !approvalId) return;
    const requestId = ++requestRef.current;
    setLoadError(null);
    try {
      const data = await fetchWsApproval(companyId, approvalId);
      if (requestId !== requestRef.current) return;
      setApproval(data);
    } catch (e: any) {
      if (requestId !== requestRef.current) return;
      const status = e?.response?.status;
      setApproval(null);
      setLoadError(status === 403 ? "forbidden" : status === 404 ? "not_found" : "failed");
    }
  }, [companyId, approvalId]);

  useEffect(() => {
    setApproval(null);
    setIndividualIds([]);
    setAddingIndividual(false);
    load();
  }, [load]);

  const addableRoles = useMemo(() => {
    if (!approval) return [];
    const taken = new Set([
      ...approval.proposal.map((role) => role.roleId),
      ...approval.currentIndividual.map((role) => role.roleId),
    ]);
    return approval.availableAreaRoles
      .filter((role) => !taken.has(role.roleId))
      .sort((a, b) => a.name.localeCompare(b.name, "lt"));
  }, [approval]);

  const backTo = withAreaQuery(`/app/${companyId}/work-security/approvals`, approval?.area?.id);

  const approve = async () => {
    if (!companyId || !approval) return;
    setApproving(true);
    try {
      await approveWsApproval(companyId, approval.id, individualIds);
      toast.success(t("ws_approvals_approved_toast", { name: approval.user?.name || "" }));
      dispatch(fetchCabinetSummaryThunk(companyId));
      navigate(`/app/${companyId}/work-security/approvals`);
    } catch (e: any) {
      const status = e?.response?.status;
      toast.error(
        status === 409
          ? t("ws_approvals_already_decided")
          : describePositionsError(e, t)
      );
      // Someone else may have decided first, or a newer approval superseded
      // this one — show what the server holds now.
      await load();
      dispatch(fetchCabinetSummaryThunk(companyId));
    } finally {
      setApproving(false);
    }
  };

  if (loadError) {
    return (
      <div className="w-full px-6 py-4">
        <Link to={`/app/${companyId}/work-security/approvals`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline max-md:min-h-11">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {t("ws_approvals_tab")}
        </Link>
        <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>
            {loadError === "forbidden"
              ? t("ws_approvals_forbidden")
              : loadError === "not_found"
                ? t("ws_approvals_not_found")
                : t("ws_approvals_load_failed")}
          </span>
          {loadError === "failed" && (
            <button type="button" className="underline" onClick={load}>
              {t("retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="flex justify-center p-10">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  const isPending = approval.status === "pending";

  return (
    <div className="w-full max-w-4xl px-6 py-4">
      <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline max-md:min-h-11">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t("ws_approvals_tab")}
      </Link>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{approval.user?.name || "—"}</h1>
        {!isPending && (
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            {approval.status === "superseded"
              ? t("ws_approvals_status_superseded")
              : approval.status === "auto_approved"
                ? t("ws_approvals_auto")
                : t("approved")}
          </span>
        )}
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-gray-200 bg-white p-4 text-sm shadow-sm sm:grid-cols-2">
        <div>
          <dt className="text-gray-500">{t("positions_title")}</dt>
          <dd className="mt-0.5 font-medium text-gray-900">{approval.positions.join(", ") || "—"}</dd>
        </div>
        <div>
          <dt className="text-gray-500">{t("type")}</dt>
          <dd className="mt-0.5 text-gray-900">{t(approvalKindKey(approval.kind))}</dd>
        </div>
        <div>
          <dt className="text-gray-500">{t("ws_approvals_col_area")}</dt>
          <dd className="mt-0.5 text-gray-900">{approval.area?.name || "—"}</dd>
        </div>
        <div>
          <dt className="text-gray-500">{t("created_at")}</dt>
          <dd className="mt-0.5 text-gray-900">{formatPrettyDate(approval.createdAt)}</dd>
        </div>
        {approval.status === "approved" && (approval.approvedBy || approval.approvedAt) && (
          <div className="sm:col-span-2">
            <dt className="text-gray-500">{t("ws_approvals_approved_by")}</dt>
            <dd className="mt-0.5 text-gray-900">
              {[approval.approvedBy?.name, approval.approvedAt ? formatPrettyDate(approval.approvedAt) : null]
                .filter(Boolean)
                .join(", ")}
            </dd>
          </div>
        )}
      </dl>

      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">{t("ws_approvals_proposal_title")}</h2>
        <p className="mt-0.5 text-xs text-gray-500">{t("ws_approvals_proposal_hint")}</p>
        {approval.proposal.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">{t("ws_approvals_proposal_empty")}</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100">
            {approval.proposal.map((role) => (
              <li key={role.roleId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <Link
                  to={`/app/${companyId}/work-security/${role.roleId}`}
                  className="font-medium text-gray-900 hover:text-blue-700"
                >
                  {role.name}
                </Link>
                {role.requiredAtHiring && (
                  <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800">
                    {t("ws_approvals_required_at_hiring")}
                  </span>
                )}
                {!role.hasActiveDocument && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3 w-3" aria-hidden />
                    {t("ws_approvals_no_document")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">{t("ws_approvals_individual_title")}</h2>
        {approval.currentIndividual.length > 0 ? (
          <p className="mt-1 text-sm text-gray-700">
            {approval.currentIndividual.map((role) => role.name).join(", ")}
          </p>
        ) : (
          <p className="mt-1 text-sm text-gray-400">{t("ws_approvals_individual_none")}</p>
        )}

        {isPending && !addingIndividual && addableRoles.length > 0 && (
          <button
            type="button"
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 max-md:min-h-11"
            onClick={() => setAddingIndividual(true)}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t("ws_approvals_add_individual")}
          </button>
        )}
        {isPending && addingIndividual && (
          <div className="mt-3">
            <p className="mb-1 text-xs text-gray-500">{t("ws_approvals_add_individual_hint")}</p>
            <div className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {addableRoles.map((role) => (
                <label
                  key={role.roleId}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={individualIds.includes(role.roleId)}
                    onChange={() =>
                      setIndividualIds((prev) =>
                        prev.includes(role.roleId)
                          ? prev.filter((id) => id !== role.roleId)
                          : [...prev, role.roleId]
                      )
                    }
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-900">{role.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </section>

      {isPending && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={approve}
            disabled={approving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 max-md:min-h-11"
          >
            {approving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            {t("ws_approvals_approve")}
          </button>
          {individualIds.length > 0 && (
            <span className="text-sm text-gray-500">
              {t("ws_approvals_individual_selected", { count: individualIds.length })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
